import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { CommonDataService } from '../common-data.service';
import { GamesService } from '../games/games.service';
import { FiltersService } from '../filters.service';
import { PlatformGraphQlService } from '../graphql/platform-graphql.service';
import { DEFAULT_LIMIT, PaginationFilterInterface } from '../../helpers/pagination-manager';

/**
 * Available transaction statuses (for frontend only)
 */
export enum TransactionStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  NEW = 'NEW',
  CANCELED = 'CANCELED',
  FAILED = 'FAILED',
  DECLINED = 'DECLINED',
  COMPLETED = 'COMPLETED',
}

export enum TransactionStatusKeys {
  PENDING = 't.pending',
  PROCESSING = 't.processing',
  NEW = 't.new',
  CANCELED = 't.canceled',
  FAILED = 't.failed',
  DECLINED = 't.declined',
  COMPLETED = 't.completed',
}

export enum TransactionActions {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
}

export enum TransactionActionsKeys {
  DEPOSIT = 't.deposit',
  WITHDRAWAL = 't.withdrawal',
}

@Injectable({
  providedIn: 'root',
})
export class UserTransactionsService {
  private _data = inject(CommonDataService);
  private _games = inject(GamesService);
  private _filters = inject(FiltersService);
  private _platformApi = inject(PlatformGraphQlService);


  get TransactionStatus() {
    return TransactionStatus;
  }

  get TransactionAction() {
    return TransactionActions;
  }

  /**
   * Returns users transactions list
   */
  transactionList(offsetParams: number, filterParams: PaginationFilterInterface): Observable<any> {
    return this._platformApi
      .paymentTransactionList({
        sitePaymentTransactionSortInput: { createdAt: 'DESC' },
        sitePaymentPaginationInput: { limit: DEFAULT_LIMIT, offset: offsetParams * DEFAULT_LIMIT || 0 },
        sitePaymentTransactionFilterInput: this._filters.removeEmptyOrFalse(filterParams),
      })
      .pipe(
        map(res => ({
          list: res.data.result.paymentTransactions.edges || [],
          totalCount: res.data.result.paymentTransactions.totalCount || 0,
        })),
        map(({ list, totalCount }) => ({
          list: list.map(transaction => {
            return {
              ...transaction.node,
              currency_symbol: this._data.currencySymbol(transaction.node.currency),
              created_at: new Date(transaction.node.createdAt),
              formattedAction: this.formatAction(transaction.node.type),
              formattedStatus: this.formatStatus(transaction.node.status),
            };
          }),
          totalCount,
        })),
        catchError(error => of([])),
      );
  }

  /**
   * Returns users bets list
   */
  betList(offsetParams: number): Observable<any> {
    const games = new Set();
    let bets = [];
    let totalCount = 0;

    return this._platformApi
      .gameTransactionList({
        siteGameTransactionSortInput: { createdAt: 'DESC' },
        paginationInput: { limit: DEFAULT_LIMIT * 2, offset: offsetParams * (DEFAULT_LIMIT * 2) || 0 },
      })
      .pipe(
        map(res => {
          return {
            list: res.data.result.gameTransactions.edges || [],
            betsTotalCount: Math.round(res.data.result.gameTransactions.totalCount / 2) || 0,
          };
        }),
        tap(({ list, betsTotalCount }) => {
          totalCount = betsTotalCount;
          bets = list.map(bet => {
            games.add((bet.node as any).game.id);
            return {
              ...bet.node,
              currency_symbol: this._data.currencySymbol(bet.node.currency),
              date: new Date(bet.node.createdAt),
            };
          });
        }),
        switchMap(() =>
          this._games.list({
            'external_id[]': [...games],
          }),
        ),
        filter(response => !!response),
        map(gameResponse => {
          const gamesByExternalId = this._filters.valueAsKeyObject('externalId', gameResponse.gameList || []);
          return {
            list: this._resolveBetsData(bets).map(bet => ({
              ...bet,
              game: gamesByExternalId[bet.game.id],
            })),
            totalCount,
          };
        }),
        catchError(error => of({})),
      );
  }

  _resolveBetsData(bets: any): any {
    for (let i = 1; i < bets.length; i += 2) {
      bets[i - 1].bet = bets[i].amount;
      bets[i - 1].win = bets[i - 1].amount;
    }

    return bets.filter((_, index) => index % 2 === 0);
  }

  /**
   * Cancel pending cash out
   *
   * @param id
   */
  cashoutRecall(transactionId: string): Observable<any> {
    return this._platformApi.cancelWithdrawal({ transactionId });
  }

  /**
   * Format transaction status
   *
   * @param status
   */
  formatStatus(status: string) {
    return `t.${status}-transaction`.toLowerCase();
  }
  /**
   * Format transaction Action
   *
   * @param status
   */
  formatAction(action: string) {
    return `t.${action}-transaction`.toLowerCase();
  }
}
